import { ProductVariant } from '@getstep/proto/common'
import type { Tier } from '@getstep/proto/tier'
import { TierType } from '@getstep/proto/tier'
import { notNullish, nullish } from '@getstep/sdk/dist/util/Assert'
import { observer } from 'mobx-react-lite'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import styles from './exclusive-offer.module.scss'
import { Button } from '@components/button/button'
import { replaceForSymbol } from '@components/markdown/utils'
import { URLS } from '@components/playbook/urls'
import { Spinner } from '@components/spinner'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { usePlaybook } from '@lib/hooks/usePlaybook'
import { useSession } from '@lib/store/useStores'

export const ExclusiveOffer = observer(() => {
    const sessionStore = useSession()
    const router = useRouter()
    const [tier, setTier] = useState<Tier | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)
    const [isScreenLoading, setIsScreenLoading] = useState(true)
    const playbook = usePlaybook()

    useEffect(() => {
        playbook.setIsLoading(isLoading)
    }, [isLoading])

    useEffect(() => {
        playbook.setHeaderText(isScreenLoading ? '' : 'Exclusive Offer')
        playbook.setHeaderBackButtonVisible(false)
        setIsLoading(false)
    }, [isScreenLoading])

    useEffectAsync(async () => {
        setIsScreenLoading(true)
        if (!sessionStore.loaded) {
            await sessionStore.restore()
        }
        if (nullish(tier)) {
            const publicApi = await sessionStore.sdk.createPublicApi()
            const tierResponse = await publicApi.getTier(
                TierType.BLACK,
                ProductVariant.META_LENDING,
            )
            setTier(tierResponse.tier)
        }
        if (notNullish(sessionStore.authorizedStore)) {
            // Use enrollment flow manager from authorized store
            await sessionStore.authorizedStore.enrollmentFlowManager.navigateToNextStep(
                router,
            )
            return
        }
        setIsScreenLoading(false)
    }, [])

    if (isScreenLoading) {
        return <Spinner />
    }

    return (
        <div className={styles.exclusiveOffer}>
            <div className={styles.imageContainer}>
                <Image
                    priority
                    src='/images/playbook/exclusive-offer-lending.webp'
                    alt='Need Cash Now?'
                    width={736}
                    height={375}
                    className={styles.image}
                />
            </div>

            <div className={styles.copyContainer}>
                <h2 className={styles.title}>Need Cash Now?</h2>
                <p>
                    Get access to cash when you need it and make the most of
                    your finances with Step EarlyPay.
                </p>

                <div className={styles.iconCopyContainer}>
                    <div className={styles.iconCopyRow}>
                        <div className={styles.iconContainer}>
                            <Image
                                src='/images/playbook/lending-icon-1.webp'
                                alt='Borrow up to $250 instantly'
                                width={40}
                                height={40}
                                className={styles.icon}
                            />
                        </div>
                        <span className={styles.copy}>
                            Borrow <strong>up to $250</strong> instantly¹
                        </span>
                    </div>
                    <div className={styles.iconCopyRow}>
                        <div className={styles.iconContainer}>
                            <Image
                                src='/images/playbook/lending-icon-2.webp'
                                alt='Help build your credit'
                                width={40}
                                height={40}
                                className={styles.icon}
                            />
                        </div>
                        <span className={styles.copy}>
                            <strong>Build credit</strong> just by using your
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: replaceForSymbol(
                                        ' Step Card ~symU+030D',
                                    ),
                                }}
                            />
                        </span>
                    </div>
                    <div className={styles.iconCopyRow}>
                        <div className={styles.iconContainer}>
                            <Image
                                src='/images/playbook/lending-icon-3.webp'
                                alt='Earn unlimited cashback on Step Visa card purchases'
                                width={40}
                                height={40}
                                className={styles.icon}
                            />
                        </div>
                        <span className={styles.copy}>
                            <strong>Get up to 10% cashback</strong> on
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: replaceForSymbol(
                                        ' purchases ~symU+02D6~symU+02D6',
                                    ),
                                }}
                            />
                        </span>
                    </div>
                </div>

                <div className={styles.disclaimer}>
                    <p>
                        ¹The availability and amount of Step EarlyPay loans are
                        subject to your eligibility and creditworthiness. Loans
                        can range from $20-$250. Instant Transfers are available
                        for a fee. Instant Transfers typically occur in seconds,
                        but may take up to 30 minutes. See{' '}
                        <a
                            href='/policies/step-borrow-cash-pay-in-one-agreement'
                            target='_blank'
                        >
                            Step EarlyPay Agreement
                        </a>{' '}
                        for details.
                    </p>
                    <p>
                        See additional terms and conditions in{' '}
                        <a
                            href='/policies/step-product-disclosures'
                            target='_blank'
                        >
                            Step Product Disclosures
                        </a>
                    </p>
                </div>

                <div className={styles.footer}>
                    <Button
                        size='medium'
                        preset='futureFuscia'
                        className={styles.button}
                        onClick={() => {
                            setIsLoading(true)
                            router.replace(URLS.PHONE_VERIFICATION)
                        }}
                        loading={isLoading}
                    >
                        Borrow Cash Now
                    </Button>
                </div>
            </div>
        </div>
    )
})

export default ExclusiveOffer
